/**
 * Client-side energy keys, blind guess helpers, collection storage.
 *
 * Production intent: 3 keys, regen 1 per 90 minutes.
 * Local feel-test (IS_LOCAL_MODE): refundEnergyLocal restores 1 key on each
 * successful solve so a solo tester can finish a full vault day without waiting.
 */

const ENERGY_KEY = 'vq_energy_v1';
const GUESSES_KEY = 'vq_guesses_v1';
const COLLECTION_KEY = 'vq_collection_v1';
const MAX_ENERGY = 3;
const REGEN_MS = 90 * 60 * 1000; // 90 minutes
const MAX_GUESSES = 3;

function today() {
    return new Date().toISOString().split('T')[0];
}

function loadEnergy() {
    try {
        const raw = JSON.parse(localStorage.getItem(ENERGY_KEY) || 'null');
        if (!raw) return { keys: MAX_ENERGY, updatedAt: Date.now() };
        let { keys, updatedAt } = raw;
        const elapsed = Date.now() - updatedAt;
        const gained = Math.floor(elapsed / REGEN_MS);
        if (gained > 0) {
            keys = Math.min(MAX_ENERGY, keys + gained);
            updatedAt += gained * REGEN_MS;
            saveEnergy({ keys, updatedAt });
        }
        return { keys, updatedAt };
    } catch {
        return { keys: MAX_ENERGY, updatedAt: Date.now() };
    }
}

function saveEnergy(state) {
    localStorage.setItem(ENERGY_KEY, JSON.stringify(state));
}

export function getEnergyState() {
    return loadEnergy();
}

export function spendEnergy() {
    const state = loadEnergy();
    if (state.keys <= 0) return { ok: false, ...state };
    const wasFull = state.keys >= MAX_ENERGY;
    state.keys -= 1;
    // Start regen clock when leaving full energy
    if (wasFull) {
        state.updatedAt = Date.now();
    }
    saveEnergy(state);
    return { ok: true, ...state };
}

/**
 * Restore 1 energy key (capped at MAX_ENERGY).
 * Used only in local mock feel-test after a successful slice solve.
 */
export function refundEnergyLocal() {
    const state = loadEnergy();
    state.keys = Math.min(MAX_ENERGY, state.keys + 1);
    saveEnergy(state);
    return { ok: true, ...state };
}

export function getGuessState(date = today()) {
    try {
        const raw = JSON.parse(localStorage.getItem(GUESSES_KEY) || '{}');
        const day = raw[date] || { remaining: MAX_GUESSES, submitted: [], correct: false };
        return day;
    } catch {
        return { remaining: MAX_GUESSES, submitted: [], correct: false };
    }
}

export function recordGuess(text, correct, date = today(), meta = {}) {
    const all = JSON.parse(localStorage.getItem(GUESSES_KEY) || '{}');
    const day = all[date] || { remaining: MAX_GUESSES, submitted: [], correct: false };
    if (day.remaining <= 0 && !day.correct) return { ok: false, ...day };
    day.remaining = Math.max(0, day.remaining - 1);
    day.submitted.push({ text, correct, at: Date.now(), ...meta });
    if (correct) {
        day.correct = true;
        day.reward = {
            gloryMult: meta.gloryMult || '1×',
            completionPct: meta.completionPct ?? null,
            at: Date.now(),
        };
    }
    all[date] = day;
    localStorage.setItem(GUESSES_KEY, JSON.stringify(all));
    return { ok: true, ...day };
}

/** Clear today's (or all) guess attempts — used by debug / reseed. */
export function resetGuessState(date = today()) {
    try {
        const all = JSON.parse(localStorage.getItem(GUESSES_KEY) || '{}');
        if (date === '*') {
            localStorage.removeItem(GUESSES_KEY);
            return { remaining: MAX_GUESSES, submitted: [], correct: false };
        }
        delete all[date];
        localStorage.setItem(GUESSES_KEY, JSON.stringify(all));
    } catch {
        localStorage.removeItem(GUESSES_KEY);
    }
    return { remaining: MAX_GUESSES, submitted: [], correct: false };
}

export function resetEnergyState() {
    const state = { keys: MAX_ENERGY, updatedAt: Date.now() };
    saveEnergy(state);
    return state;
}

export function normalizeGuess(s) {
    return String(s || '')
        .trim()
        .toLowerCase()
        .replace(/[^\p{L}\p{N}\s]/gu, '');
}

export function matchAnswers(guess, answers = []) {
    const g = normalizeGuess(guess);
    return answers.some((a) => normalizeGuess(a) === g);
}

export function getCollection() {
    try {
        return JSON.parse(localStorage.getItem(COLLECTION_KEY) || '[]');
    } catch {
        return [];
    }
}

export function unlockCollectionEntry(entry) {
    const list = getCollection();
    if (list.some((e) => e.modelId === entry.modelId)) return list;
    list.push({ ...entry, unlockedAt: Date.now() });
    localStorage.setItem(COLLECTION_KEY, JSON.stringify(list));
    return list;
}

export function hasContributedToday(date = today()) {
    return localStorage.getItem(`vq_contrib_${date}`) === '1';
}

export function markContributed(date = today()) {
    localStorage.setItem(`vq_contrib_${date}`, '1');
}

export const ENERGY_MAX = MAX_ENERGY;
export const ENERGY_REGEN_MS = REGEN_MS;
